import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;600;700;800&family=DM+Mono:wght@400;500&display=swap');
.tax-wrap * { box-sizing: border-box; }
.tax-wrap { font-family: 'Bricolage Grotesque', system-ui, sans-serif; background: #f6f7f9; min-height: 100vh; padding: 28px 24px 80px; }
.tax-banner{background:linear-gradient(160deg,#064e3b 0%,#0d9488 35%,#14b8a6 65%,#0369a1 100%);padding:28px 32px 32px;display:flex;align-items:center;justify-content:space-between;gap:16px;box-shadow:0 14px 0 rgba(6,78,59,.22),0 24px 48px rgba(13,148,136,.38),inset 0 1px 0 rgba(255,255,255,.18),inset 0 -2px 0 rgba(0,0,0,.12);position:relative;overflow:hidden;margin:-28px -24px 28px;flex-wrap:wrap;}
.tax-banner::before{content:'';position:absolute;top:-50px;right:-50px;width:220px;height:220px;background:rgba(255,255,255,.06);border-radius:50%;pointer-events:none;}
.tax-banner h1{font-size:28px;font-weight:800;color:#fff;margin:0 0 4px;letter-spacing:-.02em;font-family:'Bricolage Grotesque',sans-serif;text-shadow:0 2px 8px rgba(0,0,0,.18);}
.tax-banner p{font-size:13px;color:rgba(255,255,255,.8);margin:0;font-weight:500;}
.tax-banner-right{display:flex;gap:10px;align-items:center;flex-shrink:0;flex-wrap:wrap;}
.tax-year-sel{background:rgba(255,255,255,.15);border:1.5px solid rgba(255,255,255,.35);border-radius:9px;padding:9px 14px;font-size:14px;font-weight:600;color:#fff;cursor:pointer;font-family:'Bricolage Grotesque',sans-serif;outline:none;}
.tax-year-sel option{background:#1e293b;color:#fff;}
.tax-export-btn{background:#fff;color:#0d9488;border:none;border-radius:10px;padding:10px 20px;font-weight:800;font-size:14px;cursor:pointer;font-family:'Bricolage Grotesque',sans-serif;box-shadow:0 4px 14px rgba(0,0,0,.12);transition:all .15s;white-space:nowrap;}
.tax-export-btn:hover{transform:translateY(-2px);box-shadow:0 6px 20px rgba(0,0,0,.18);}
.tax-export-btn:disabled{opacity:.5;cursor:default;transform:none;}
@media(max-width:600px){.tax-banner{border-radius:0;padding:18px 16px 22px;margin:-14px -12px 20px;}.tax-banner h1{font-size:20px;}}
`;

interface TaxData {
  tax_year: string;
  date_from: string;
  date_to: string;
  total_income: number;
  total_rent_received: number;
  total_expenses: number;
  net_profit: number;
  transactions: any[];
  per_property: { address: string; income: number; expenses: number; net: number }[];
  by_category: { category: string; total: number }[];
}

const fmt = (n: number) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 };
const card: React.CSSProperties = { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '20px 24px' };

function getYears() {
  const now = new Date();
  const current = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const years = [];
  for (let y = current; y >= current - 5; y--) {
    years.push(y);
  }
  return years;
}

export default function TaxReport() {
  const { token } = useAuth();
  const years = getYears();
  const [year, setYear] = useState(years[0]);
  const [data, setData] = useState<TaxData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTx, setShowTx] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/landlord/tax-report?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
      setData(await r.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [year, token]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ['ShahoSuite — UK Tax Year Report'],
      [`Tax Year: ${data.tax_year} (${data.date_from} to ${data.date_to})`],
      [],
      ['SUMMARY'],
      ['Total Rental Income', fmt(data.total_income)],
      ['Total Allowable Expenses', fmt(data.total_expenses)],
      ['Net Profit (HMRC Taxable)', fmt(data.net_profit)],
      [],
      ['PER-PROPERTY BREAKDOWN'],
      ['Property', 'Income', 'Expenses', 'Net Profit'],
      ...data.per_property.map(p => [p.address, fmt(p.income), fmt(p.expenses), fmt(p.net)]),
      [],
      ['EXPENSES BY CATEGORY'],
      ['Category', 'Total'],
      ...data.by_category.map(c => [c.category, fmt(c.total)]),
      [],
      ['ALL TRANSACTIONS'],
      ['Date', 'Property', 'Type', 'Category', 'Description', 'Amount'],
      ...data.transactions.map(t => [
        t.date?.split('T')[0], t.property_address, t.type, t.category, t.description, fmt(t.amount)
      ]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ShahoSuite-TaxReport-${data.tax_year.replace('/', '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const netColor = data && data.net_profit >= 0 ? '#059669' : '#dc2626';

  return (
    <div className="tax-wrap">
      <style>{CSS}</style>

      {/* Banner */}
      <div className="tax-banner">
        <div>
          <h1>📊 UK Tax Year Report</h1>
          <p>HMRC Self Assessment summary — rental income &amp; allowable expenses</p>
        </div>
        <div className="tax-banner-right">
          <select className="tax-year-sel" value={year} onChange={e => setYear(Number(e.target.value))}>
            {years.map(y => (
              <option key={y} value={y}>{y}/{String(y + 1).slice(2)} (6 Apr {y} – 5 Apr {y + 1})</option>
            ))}
          </select>
          <button className="tax-export-btn" onClick={exportCSV} disabled={!data}>⬇ Export CSV</button>
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 16px', color: '#dc2626', marginBottom: 16 }}>{error}</div>}

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading tax report…</div>}

      {data && !loading && (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
            <div style={{ ...card, borderTop: '4px solid #0d9488' }}>
              <span style={lbl}>Total Rental Income</span>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#0d9488', fontFamily: 'DM Mono, monospace' }}>{fmt(data.total_income)}</div>
              {data.total_rent_received > 0 && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>incl. {fmt(data.total_rent_received)} rent collected</div>}
            </div>
            <div style={{ ...card, borderTop: '4px solid #f59e0b' }}>
              <span style={lbl}>Allowable Expenses</span>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#b45309', fontFamily: 'DM Mono, monospace' }}>{fmt(data.total_expenses)}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{data.by_category.length} categories</div>
            </div>
            <div style={{ ...card, borderTop: `4px solid ${netColor}` }}>
              <span style={lbl}>Net Taxable Profit</span>
              <div style={{ fontSize: 28, fontWeight: 800, color: netColor, fontFamily: 'DM Mono, monospace' }}>{fmt(data.net_profit)}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Enter on HMRC SA105</div>
            </div>
            <div style={{ ...card, borderTop: '4px solid #7c3aed' }}>
              <span style={lbl}>Properties</span>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#7c3aed', fontFamily: 'DM Mono, monospace' }}>{data.per_property.length}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{data.transactions.length} transactions</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
            {/* Per-Property Breakdown */}
            <div style={card}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#111827' }}>🏠 Per-Property Breakdown</h3>
              {data.per_property.length === 0 && <p style={{ color: '#9ca3af', fontSize: 13 }}>No transactions this tax year.</p>}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f3f4f6' }}>
                    <th style={{ textAlign: 'left', padding: '6px 0', color: '#6b7280', fontWeight: 600 }}>Property</th>
                    <th style={{ textAlign: 'right', padding: '6px 0', color: '#6b7280', fontWeight: 600 }}>Income</th>
                    <th style={{ textAlign: 'right', padding: '6px 0', color: '#6b7280', fontWeight: 600 }}>Expenses</th>
                    <th style={{ textAlign: 'right', padding: '6px 0', color: '#6b7280', fontWeight: 600 }}>Net</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_property.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 0', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' }}>{p.address}</td>
                      <td style={{ padding: '8px 0', textAlign: 'right', color: '#059669', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{fmt(p.income)}</td>
                      <td style={{ padding: '8px 0', textAlign: 'right', color: '#b45309', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{fmt(p.expenses)}</td>
                      <td style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 700, color: p.net >= 0 ? '#059669' : '#dc2626' }}>{fmt(p.net)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 0', fontWeight: 700 }}>Total</td>
                    <td style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 700, color: '#059669' }}>{fmt(data.total_income)}</td>
                    <td style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 700, color: '#b45309' }}>{fmt(data.total_expenses)}</td>
                    <td style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 700, color: netColor }}>{fmt(data.net_profit)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Expense Categories */}
            <div style={card}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#111827' }}>📋 Expenses by Category</h3>
              {data.by_category.length === 0 && <p style={{ color: '#9ca3af', fontSize: 13 }}>No expenses this tax year.</p>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.by_category.map((c, i) => {
                  const pct = data.total_expenses > 0 ? (c.total / data.total_expenses) * 100 : 0;
                  return (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
                        <span style={{ color: '#374151' }}>{c.category}</span>
                        <span style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600, color: '#b45309' }}>{fmt(c.total)}</span>
                      </div>
                      <div style={{ height: 5, background: '#f3f4f6', borderRadius: 4 }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: '#f59e0b', borderRadius: 4 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* HMRC Guidance */}
          <div style={{ background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: 10, padding: '14px 20px', marginBottom: 24 }}>
            <strong style={{ color: '#92400e', fontSize: 13 }}>📌 HMRC SA105 Guidance</strong>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#78350f' }}>
              Report this profit on your <strong>Self Assessment SA105 (UK Property)</strong> page. Allowable expenses include maintenance, management fees, insurance, mortgage interest (as finance costs), and letting agent fees. 
              Personal use portions and capital improvements are not deductible. Consult a qualified accountant for advice specific to your situation.
            </p>
          </div>

          {/* Transaction Log */}
          <div style={card}>
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              onClick={() => setShowTx(v => !v)}
            >
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>
                🧾 All Transactions ({data.transactions.length})
              </h3>
              <span style={{ fontSize: 13, color: '#6b7280' }}>{showTx ? '▲ Hide' : '▼ Show'}</span>
            </div>
            {showTx && (
              <div style={{ marginTop: 16, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #f3f4f6', background: '#f9fafb' }}>
                      {['Date', 'Property', 'Type', 'Category', 'Description', 'Amount'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Amount' ? 'right' : 'left', color: '#6b7280', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.transactions.map((t, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '7px 10px', color: '#6b7280', whiteSpace: 'nowrap' }}>{t.date?.split('T')[0]}</td>
                        <td style={{ padding: '7px 10px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.property_address}</td>
                        <td style={{ padding: '7px 10px' }}>
                          <span style={{ background: t.type === 'income' ? '#d1fae5' : '#fee2e2', color: t.type === 'income' ? '#059669' : '#dc2626', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{t.type}</span>
                        </td>
                        <td style={{ padding: '7px 10px', color: '#6b7280' }}>{t.category}</td>
                        <td style={{ padding: '7px 10px' }}>{t.description}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600, color: t.type === 'income' ? '#059669' : '#dc2626' }}>{fmt(t.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
